import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Award, CheckCircle2, Clock, AlertTriangle, ArrowRight, Sparkles, Loader2,
} from 'lucide-react';

/**
 * Iter-225d / Phase 1C-B — Talent dashboard certifications widget.
 *
 * Renders four buckets:
 *   • Pending      — modules mapped to a role the talent has touched but never started
 *   • In Progress  — modules with active training_progress
 *   • Completed    — earned certifications still valid
 *   • Expired      — earned certifications past expires_at
 *
 * Data source: GET /api/training/my-status (talent-only).
 * Empty buckets gracefully hidden; widget renders nothing if the talent has
 * NO touched roles AND no earned/in-progress certs (fresh accounts).
 */
const BUCKETS = [
  {
    key: 'pending',
    label: 'Pending',
    color: 'cyan',
    icon: Sparkles,
    cta: 'Take assessment',
  },
  {
    key: 'in_progress',
    label: 'In Progress',
    color: 'amber',
    icon: Clock,
    cta: 'Resume',
  },
  {
    key: 'completed',
    label: 'Completed',
    color: 'emerald',
    icon: CheckCircle2,
    cta: 'View badge',
  },
  {
    key: 'expired',
    label: 'Expired',
    color: 'red',
    icon: AlertTriangle,
    cta: 'Renew',
  },
];

const COLOR_CLASSES = {
  cyan: {
    bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-800',
    pill: 'bg-cyan-100 text-cyan-800', btn: 'bg-cyan-500 hover:bg-cyan-600 text-white',
  },
  amber: {
    bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800',
    pill: 'bg-amber-100 text-amber-800', btn: 'bg-amber-500 hover:bg-amber-600 text-white',
  },
  emerald: {
    bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800',
    pill: 'bg-emerald-100 text-emerald-800', btn: 'bg-emerald-500 hover:bg-emerald-600 text-white',
  },
  red: {
    bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800',
    pill: 'bg-red-100 text-red-800', btn: 'bg-red-500 hover:bg-red-600 text-white',
  },
};

const TalentCertificationsWidget = ({ className = '' }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/training/my-status');
        if (!cancelled) setData(r.data);
      } catch (_) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Card className={className} data-testid="cert-widget-loading">
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;
  const totalAcross = (data.totals?.pending || 0) + (data.totals?.in_progress || 0)
                    + (data.totals?.completed || 0) + (data.totals?.expired || 0);
  if (totalAcross === 0) return null;

  return (
    <Card
      className={`bg-white border border-gray-200 ${className}`}
      data-testid="talent-certifications-widget"
    >
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Award className="w-5 h-5 text-cyan-600" />
          Your Certifications
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Bucket summary pills */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="cert-bucket-summary">
          {BUCKETS.map(({ key, label, color, icon: Icon }) => {
            const count = data.totals?.[key] || 0;
            const c = COLOR_CLASSES[color];
            return (
              <div
                key={key}
                className={`${c.bg} ${c.border} border rounded-lg p-2 text-center`}
                data-testid={`cert-bucket-pill-${key}`}
              >
                <Icon className={`w-4 h-4 mx-auto mb-1 ${c.text}`} />
                <div className={`text-xl font-bold ${c.text}`}>{count}</div>
                <div className={`text-[10px] uppercase tracking-wide ${c.text}`}>{label}</div>
              </div>
            );
          })}
        </div>

        {/* Per-bucket detail — only render non-empty buckets */}
        {BUCKETS.map(({ key, label, color, cta }) => {
          const items = data[key] || [];
          if (items.length === 0) return null;
          const c = COLOR_CLASSES[color];
          return (
            <div key={key} data-testid={`cert-bucket-list-${key}`}>
              <p className={`text-xs font-semibold uppercase tracking-wide ${c.text} mb-1.5`}>
                {label} ({items.length})
              </p>
              <ul className="space-y-1.5">
                {items.slice(0, 3).map((item) => (
                  <li
                    key={item.module_id}
                    className={`flex items-center justify-between gap-2 ${c.bg} ${c.border} border rounded-md px-3 py-2`}
                    data-testid={`cert-row-${key}-${item.module_id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {item.badge_name}
                      </div>
                      <div className="text-[11px] text-gray-600 truncate">
                        {item.role_name && <span>{item.role_name}</span>}
                        {key === 'completed' && item.earned_at && (
                          <span> · earned {item.earned_at}</span>
                        )}
                        {key === 'expired' && item.expired_at && (
                          <span> · expired {item.expired_at}</span>
                        )}
                        {key === 'in_progress' && item.last_activity_at && (
                          <span> · last active {item.last_activity_at}</span>
                        )}
                      </div>
                    </div>
                    {key !== 'completed' && (
                      <Button
                        size="sm"
                        onClick={() => navigate(item.assessment_url)}
                        className={`${c.btn} text-xs h-7`}
                        data-testid={`cert-row-cta-${key}-${item.module_id}`}
                      >
                        {cta} <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    )}
                    {key === 'completed' && (
                      <Badge className={`${c.pill} border-0 text-[10px]`}>
                        ✓ {item.score != null ? `${item.score}%` : 'Earned'}
                      </Badge>
                    )}
                  </li>
                ))}
                {items.length > 3 && (
                  <li className="text-xs text-gray-500 text-center pt-1">
                    +{items.length - 3} more
                  </li>
                )}
              </ul>
            </div>
          );
        })}

        <div className="pt-2 border-t border-gray-100">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/talent/training')}
            className="w-full text-cyan-600 hover:bg-cyan-50"
            data-testid="cert-widget-view-all"
          >
            Browse all certifications <ArrowRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default TalentCertificationsWidget;
